/**
 * Filter Toggle Switch Component
 * Handles the toggle between "Gamme" and "Recommandé pour" filters
 * with mutually exclusive behavior
 */

(function() {
  'use strict';

  function getSwitchParamKeys(container) {
    const paramsEl = container.querySelector('[data-switch-params]');
    if (!paramsEl || !paramsEl.dataset.switchParams) return [];
    return paramsEl.dataset.switchParams
      .split('|')
      .map(item => item.trim())
      .filter(Boolean);
  }

  function getGlobalSwitchParamKeys() {
    const paramsEl = document.querySelector('[data-switch-params]');
    if (!paramsEl || !paramsEl.dataset.switchParams) return [];
    return paramsEl.dataset.switchParams
      .split('|')
      .map(item => item.trim())
      .filter(Boolean);
  }

  function sanitizeSearchParams(searchParams, mode, switchKeys) {
    const params = new URLSearchParams(searchParams || '');
    const keys = Array.from(params.keys());

    if (mode === 'switch') {
      keys.forEach(key => {
        if (key.startsWith('filter.') && !switchKeys.includes(key)) {
          params.delete(key);
        }
      });
    } else if (mode === 'standard') {
      switchKeys.forEach(key => params.delete(key));
    }

    return params.toString();
  }

  function getBarState(searchInput) {
    const switchKeys = getGlobalSwitchParamKeys();
    const normalized = typeof searchInput === 'string'
      ? searchInput.replace(/^\?/, '')
      : window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(normalized);
    const keys = Array.from(params.keys());
    const hasSwitch = switchKeys.some(key => params.has(key));
    const hasStandardFromParams = keys.some(key => key.startsWith('filter.') && !switchKeys.includes(key));
    const hasStandardFromDom = document.querySelector('.collection-filter-bar__active-filters .js-facet-remove') !== null;
    const hasStandard = hasStandardFromParams || hasStandardFromDom;
    return { hasSwitch, hasStandard };
  }

  function updateBarVisibility(searchInput) {
    const state = getBarState(searchInput);

    document.querySelectorAll('.collection-filter-bar').forEach(bar => {
      bar.classList.toggle('is-standard-active', state.hasStandard);
      bar.classList.toggle('is-switch-active', state.hasSwitch && !state.hasStandard);
      bar.classList.toggle('is-empty-active', !state.hasSwitch && !state.hasStandard);
    });
  }

  function normalizeFilterValue(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function paramContainsValue(params, key, value) {
    const target = normalizeFilterValue(value);
    if (!target || !key) return false;
    const rawValues = params.getAll(key);
    for (let i = 0; i < rawValues.length; i += 1) {
      const splitValues = rawValues[i].split(',');
      for (let j = 0; j < splitValues.length; j += 1) {
        if (normalizeFilterValue(splitValues[j]) === target) {
          return true;
        }
      }
    }
    return false;
  }

  function syncSwitchPillsState(root, searchInput) {
    if (!root) return;
    const normalized = typeof searchInput === 'string'
      ? searchInput.replace(/^\?/, '')
      : window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(normalized);
    const pills = root.querySelectorAll('.filter-toggle-switch__pill');

    pills.forEach(pill => {
      const paramName = pill.getAttribute('data-filter-param');
      const value = pill.getAttribute('data-filter-value');
      const isActive = paramContainsValue(params, paramName, value);

      pill.classList.toggle('filter-toggle-switch__pill--active', isActive);
      pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      pill.setAttribute(
        'href',
        isActive
          ? (pill.getAttribute('data-filter-url-remove') || '#')
          : (pill.getAttribute('data-filter-url-add') || pill.getAttribute('href') || '#')
      );

      const existingClose = pill.querySelector('.active-facets__close');
      if (isActive && !existingClose) {
        const close = document.createElement('span');
        close.className = 'active-facets__close';
        close.setAttribute('aria-hidden', 'true');
        close.textContent = '×';
        pill.appendChild(close);
      } else if (!isActive && existingClose) {
        existingClose.remove();
      }
    });
  }

  function ensureFacetWrapper() {
    if (typeof FacetFiltersForm === 'undefined' || typeof FacetFiltersForm.renderPage !== 'function') {
      return;
    }
    if (FacetFiltersForm.__toggleWrapped) {
      return;
    }

    const originalRenderPage = FacetFiltersForm.renderPage;
    FacetFiltersForm.renderPage = function(searchParams, event, updateURLHash = true) {
      const switchKeys = getGlobalSwitchParamKeys();
      const mode = window.__filterToggleMode === 'switch' ? 'switch' : 'standard';
      const sanitized = sanitizeSearchParams(searchParams, mode, switchKeys);
      window.__filterToggleMode = null;
      const result = originalRenderPage.call(this, sanitized, event, updateURLHash);
      setTimeout(function() {
        syncSwitchPillsState(document, sanitized);
        updateBarVisibility(sanitized);
      }, 0);
      return result;
    };

    FacetFiltersForm.__toggleWrapped = true;
  }

  function initFilterToggleSwitch() {
    ensureFacetWrapper();
    const containers = document.querySelectorAll('filter-toggle-switch, [data-filter-toggle-switch]');
    
    containers.forEach(container => {
      // Skip if already initialized
      if (container.dataset.initialized === 'true') return;
      container.dataset.initialized = 'true';
      
      const toggleButtons = container.querySelectorAll('[data-toggle-target]');
      const pillsGroups = container.querySelectorAll('[data-pills-group]');
      const switchKeys = getSwitchParamKeys(container);
      const toggleShell = container.querySelector('.filter-toggle-switch__toggle');

      if (toggleShell && !toggleShell.dataset.active) {
        const activeBtn = container.querySelector('.filter-toggle-switch__btn--active') || toggleButtons[0];
        if (activeBtn) {
          toggleShell.setAttribute('data-active', activeBtn.getAttribute('data-toggle-target'));
        }
      }
      syncSwitchPillsState(container);
      updateToggleIndicator(container);
      
      // Handle toggle button clicks
      toggleButtons.forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          const targetGroup = this.getAttribute('data-toggle-target');
          const isCurrentlyActive = this.classList.contains('filter-toggle-switch__btn--active');
          
          // Get the other group
          const otherButton = Array.from(toggleButtons).find(b => b !== this);
          const otherGroup = otherButton ? otherButton.getAttribute('data-toggle-target') : null;
          
          // Check if other group has active filters
          if (otherGroup) {
            const otherPillsContainer = container.querySelector(`[data-pills-group="${otherGroup}"]`);
            const activePillsInOther = otherPillsContainer ? 
              otherPillsContainer.querySelectorAll('.filter-toggle-switch__pill--active') : [];
            
            if (activePillsInOther.length > 0) {
              // Clear other filters and switch
              clearFiltersAndSwitch(otherPillsContainer, targetGroup, switchKeys);
              return;
            }
          }
          
          // Switch to target group
          if (!isCurrentlyActive) {
            switchToGroup(container, targetGroup);
          }
        });
      });
      
      // Handle pill clicks using event delegation on the container
      container.addEventListener('click', function(e) {
        const pill = e.target.closest('.filter-toggle-switch__pill');
        if (!pill) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const isActive = pill.classList.contains('filter-toggle-switch__pill--active');
        const targetUrl = isActive 
          ? pill.getAttribute('data-filter-url-remove') 
          : (pill.getAttribute('data-filter-url-add') || pill.getAttribute('href'));
        
        if (!targetUrl) {
          console.warn('Filter toggle: No URL found for pill');
          return;
        }
        
        // Store active group for state restoration
        const pillsGroup = pill.closest('[data-pills-group]');
        if (pillsGroup) {
          sessionStorage.setItem('filterToggleSwitchTarget', pillsGroup.getAttribute('data-pills-group'));
        }
        
        // Navigate as switch action (keeps switch params, clears standard params)
        navigateToFilter(targetUrl, 'switch');
      });
      
      // Restore state if needed
      const savedGroup = sessionStorage.getItem('filterToggleSwitchTarget');
      if (savedGroup) {
        sessionStorage.removeItem('filterToggleSwitchTarget');
        switchToGroup(container, savedGroup);
      }

      // Keep indicator aligned on viewport changes
      window.addEventListener('resize', function() {
        updateToggleIndicator(container);
      });
    });

    updateBarVisibility();
  }
  
  function switchToGroup(container, targetGroup) {
    const toggleButtons = container.querySelectorAll('[data-toggle-target]');
    const pillsGroups = container.querySelectorAll('[data-pills-group]');
    const toggleShell = container.querySelector('.filter-toggle-switch__toggle');
    
    // Update toggle buttons
    toggleButtons.forEach(btn => {
      const isTarget = btn.getAttribute('data-toggle-target') === targetGroup;
      btn.classList.toggle('filter-toggle-switch__btn--active', isTarget);
      btn.setAttribute('aria-pressed', isTarget ? 'true' : 'false');
    });

    if (toggleShell) {
      toggleShell.setAttribute('data-active', targetGroup);
    }
    updateToggleIndicator(container);
    
    // Update pills visibility
    pillsGroups.forEach(group => {
      const isTarget = group.getAttribute('data-pills-group') === targetGroup;
      group.classList.toggle('filter-toggle-switch__pills--visible', isTarget);
      if (isTarget) {
        group.removeAttribute('hidden');
      } else {
        group.setAttribute('hidden', '');
      }
    });
  }
  
  function clearFiltersAndSwitch(pillsContainer, targetGroup, switchKeys) {
    const currentParams = new URLSearchParams(window.location.search);
    const filterParam = pillsContainer.getAttribute('data-filter-param');
    
    // Remove filter params
    const keysToDelete = [];
    currentParams.forEach((value, key) => {
      if (key.includes(filterParam)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => currentParams.delete(key));
    
    sessionStorage.setItem('filterToggleSwitchTarget', targetGroup);
    
    const newParams = sanitizeSearchParams(currentParams.toString(), 'switch', switchKeys);
    const newUrl = newParams
      ? `${window.location.pathname}?${newParams}`
      : window.location.pathname;

    navigateToFilter(newUrl, 'switch');
  }

  function updateToggleIndicator(container) {
    const toggleShell = container.querySelector('.filter-toggle-switch__toggle');
    if (!toggleShell) return;

    const activeButton = toggleShell.querySelector('.filter-toggle-switch__btn--active')
      || toggleShell.querySelector('.filter-toggle-switch__btn');
    if (!activeButton) return;

    const shellRect = toggleShell.getBoundingClientRect();
    const activeRect = activeButton.getBoundingClientRect();

    const left = Math.max(0, activeRect.left - shellRect.left);
    const width = activeRect.width;
    const height = Math.max(0, shellRect.height - 8); // 2 * 4px offset

    toggleShell.style.setProperty('--toggle-pill-left', `${left}px`);
    toggleShell.style.setProperty('--toggle-pill-width', `${width}px`);
    toggleShell.style.setProperty('--toggle-pill-height', `${height}px`);
  }
  
  function navigateToFilter(targetUrl, mode) {
    // Try AJAX navigation first
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      try {
        const urlObj = new URL(targetUrl, window.location.origin);
        const searchParams = urlObj.search ? urlObj.search.slice(1) : '';
        window.__filterToggleMode = mode || null;
        FacetFiltersForm.renderPage(searchParams);
        syncSwitchPillsState(document, searchParams);
        updateBarVisibility(searchParams);
        return;
      } catch (e) {
        console.warn('AJAX navigation failed:', e);
      }
    }
    
    // Fallback to full page navigation
    window.location.href = targetUrl;
  }

  function clearSwitchFiltersForStandard() {
    const switchKeys = getGlobalSwitchParamKeys();
    if (!switchKeys.length) return;

    const currentParams = new URLSearchParams(window.location.search);
    let hasSwitchFilters = false;

    switchKeys.forEach(key => {
      if (currentParams.has(key)) {
        currentParams.delete(key);
        hasSwitchFilters = true;
      }
    });

    if (hasSwitchFilters) {
      const nextSearch = currentParams.toString();
      const nextUrl = nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname;
      navigateToFilter(nextUrl, 'standard');
    } else {
      updateBarVisibility(currentParams.toString());
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterToggleSwitch);
  } else {
    initFilterToggleSwitch();
  }

  // Listen for "Filtrer et trier" button clicks to reset switch filters
  document.addEventListener('click', function(e) {
    const filterSortBtn = e.target.closest('#collection-filters-handle-drawer');
    if (!filterSortBtn) return;
    clearSwitchFiltersForStandard();
  });

  // Also listen on sidebar-drawer open to reset switch filters
  document.addEventListener('drawer-open', function(e) {
    const drawer = e.target;
    if (drawer && drawer.querySelector('[data-facet-filter-form]')) {
      clearSwitchFiltersForStandard();
    }
  });
  // Re-initialize after AJAX updates (observe DOM changes)
  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    let shouldRefreshVisibility = false;
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length)) {
        shouldRefreshVisibility = true;
      }
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1 && (
          node.matches && node.matches('filter-toggle-switch, [data-filter-toggle-switch]') ||
          node.querySelector && node.querySelector('filter-toggle-switch, [data-filter-toggle-switch]')
        )) {
          shouldReinit = true;
        }
      });
    });
    if (shouldReinit) {
      setTimeout(initFilterToggleSwitch, 100);
      return;
    }
    if (shouldRefreshVisibility) {
      setTimeout(function() {
        ensureFacetWrapper();
        syncSwitchPillsState(document);
        updateBarVisibility();
      }, 0);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });

})();
